import React, { KeyboardEvent, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';

import useEventListener from '@use-it/event-listener';
import classNames from 'classnames';

import { isEscapeKey } from '../../utils/keyboard';

import styles from './Modal.scss';
import IconButton from '../IconButton/IconButton';
import { closeIcon } from '@justgiving/icons';
import Svg from '@justgiving/svg';
import { useTranslation } from 'react-i18next';

interface ModalDialogProps {
  background?: boolean;
  focusLock?: boolean;
  scrollIsolation?: boolean;
  scrollLock?: boolean;
  onCloseRequest?: () => void;
  className?: string;
  modalTitle?: string;
  displayCloseButton?: boolean;
  closeOnClickOutsideModal?: boolean;
  closeButtonClassName?: string;
  removeScrollbar?: boolean;
  onOverlayClick?: (event: React.MouseEvent) => void;
  onModalClick?: (event: React.MouseEvent) => void;
}

const Modal: React.FunctionComponent<ModalDialogProps> = ({
  background = true,
  children,
  focusLock = true,
  scrollIsolation = true,
  scrollLock = true,
  removeScrollbar = false,
  onCloseRequest = () => {},
  className,
  modalTitle,
  displayCloseButton,
  closeOnClickOutsideModal = true,
  closeButtonClassName,
  onOverlayClick = () => {},
  onModalClick = () => {},
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const childrenContainer = useRef<HTMLDivElement>(null);

  const closeModal = (event: React.SyntheticEvent) => {
    if (
      !childrenContainer ||
      childrenContainer.current?.contains(event.target as Element)
    ) {
      return;
    }
    const JGCookieConsentBar = document.querySelector(
      '.jg-chrome-cookie-consent',
    );
    if (JGCookieConsentBar?.contains(event.target as Element)) {
      return;
    }

    onCloseRequest();
  };

  useEventListener(
    'mousedown',
    closeOnClickOutsideModal ? closeModal : () => {},
  );

  const handleKeyPressed = (event: KeyboardEvent) => {
    if (isEscapeKey(event)) {
      onCloseRequest();
    }
  };
  useEventListener('keydown', handleKeyPressed);

  return (
    <RemoveScroll
      enabled={scrollLock}
      noIsolation={scrollIsolation}
      removeScrollBar={removeScrollbar}
    >
      <div
        data-qa="modal-backdrop"
        className={classNames({ [styles.background]: background })}
        onClick={onOverlayClick}
      >
        <FocusLock
          disabled={!focusLock}
          returnFocus
          persistentFocus
          className={styles.focusLock}
        >
          <div
            role="dialog"
            aria-modal="true"
            title={modalTitle}
            ref={childrenContainer}
            className={classNames(styles.modalContainer, className)}
            onClick={onModalClick}
          >
            {displayCloseButton ? (
              <IconButton
                className={classNames(styles.close, closeButtonClassName)}
                onClick={event => {
                  event.stopPropagation();
                  onCloseRequest();
                }}
                aria-label={t('common.close')}
              >
                <Svg markup={closeIcon} />
              </IconButton>
            ) : null}
            {children}
          </div>
        </FocusLock>
      </div>
    </RemoveScroll>
  );
};

export default Modal;
